import { Sparkles, TrendingUp, Star, Shield } from "lucide-react";
import { DashboardInsights, PERIOD_LABELS, type PeriodDays } from "../../../../../../hooks/useDashboardInsights";
import { Skeleton } from "../../../../../ui/skeleton";

interface ImpactBannerProps {
  insights: DashboardInsights | null;
  loading: boolean;
  periodDays: PeriodDays;
}

export const ImpactBanner = ({ insights, loading, periodDays }: ImpactBannerProps) => {
  const labels = PERIOD_LABELS[periodDays];
  if (loading) {
    return (
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-primary/90 to-primary p-6">
        <Skeleton className="h-6 w-3/4 bg-primary-foreground/20" />
      </div>
    );
  }

  if (!insights || insights.total_reviews === 0) {
    return (
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-primary/90 to-primary p-6 text-primary-foreground">
        <div className="relative flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-foreground/10">
            <Sparkles className="h-5 w-5" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium opacity-80">Weekly Impact Summary</p>
            <p className="text-lg font-semibold leading-snug">No data available yet — start syncing reviews to see your weekly impact.</p>
          </div>
        </div>
      </div>
    );
  }

  const growth = insights.review_growth;
  const ratingDelta = insights.rating_delta;
  const intercepted = insights.intercepted_reviews;

  const parts: string[] = [];
  if (growth > 0) parts.push(`gained +${growth} new reviews`);
  else if (growth === 0) parts.push("maintained your review volume");
  if (ratingDelta > 0) parts.push(`your overall rating improved by +${ratingDelta}⭐`);
  else if (ratingDelta < 0) parts.push(`your overall rating dipped by ${ratingDelta}⭐`);
  if (intercepted > 0) parts.push(`identified ${intercepted} negative experiences for recovery`);

  const message = parts.length > 0
    ? `In the ${labels.current}, you ${parts.join(", ")}.`
    : `Keep collecting reviews to unlock ${labels.short} insights.`;

  return (
    <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-primary/90 to-primary p-6 text-primary-foreground">
      <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-primary-foreground/5" />
      <div className="absolute -right-4 -bottom-4 h-20 w-20 rounded-full bg-primary-foreground/5" />
      <div className="relative flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-foreground/10">
          <Sparkles className="h-5 w-5" />
        </div>
        <div className="space-y-1">
           <p className="text-sm font-medium opacity-80">{periodDays === 7 ? "Weekly" : periodDays === 30 ? "Monthly" : "Quarterly"} Impact Summary</p>
          <p className="text-lg font-semibold leading-snug">{message}</p>
          <div className="flex items-center gap-4 pt-2 text-sm opacity-75">
            <span className="flex items-center gap-1"><TrendingUp className="h-3.5 w-3.5" /> {insights.this_period_count} reviews {labels.current}</span>
            <span className="flex items-center gap-1"><Star className="h-3.5 w-3.5" /> {insights.overall_avg_rating}⭐ overall</span>
            <span className="flex items-center gap-1"><Shield className="h-3.5 w-3.5" /> {insights.response_rate}% managed</span>
          </div>
        </div>
      </div>
    </div>
  );
};
